import type {ReactElement} from 'react';
import React, {useCallback, useEffect, useMemo, useRef, useState, useSyncExternalStore} from 'react';
import {createPortal} from 'react-dom';
import type {Component, Editor} from 'grapesjs';
import {ColorPickerPopup} from '@Components/color-picker-popup';
import useOutsideClick from '@Hooks/useOutsideClick';
import {DEFAULT_TEXT_COLOR} from '@FormMakerReact/layout/form-maker-right-panel/sectors/sectors.constants';
import {colorToHexString} from '@Utils/color.util';
import {useSelectedDescription} from './use-selected-description';
import {applyColor} from './description-rte-formatting';
import {getSavedDescriptionRange} from './description-rte-actions';

const POPUP_GAP_PX = 12;

interface DescriptionRteColorActionProps {
  editor: Editor;
}

export function DescriptionRteColorAction({editor}: DescriptionRteColorActionProps): ReactElement | null {
  const description = useSelectedDescription(editor);
  if (!description) {
    return null;
  }

  return <ColorPicker editor={editor} component={description} />;
}

interface ColorPickerProps {
  editor: Editor;
  component: Component;
}

function ColorPicker({editor, component}: ColorPickerProps): ReactElement {
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const swatchRef = useRef<HTMLButtonElement>(null);
  const popupContainerRef = useRef<HTMLDivElement | null>(null);
  const currentColor = useCurrentTextColor(editor, component);
  const [liveColor, setLiveColor] = useState<string>(currentColor);

  useEffect((): void => {
    setLiveColor(currentColor);
  }, [currentColor]);

  useOutsideClick(popupContainerRef, (): void => {
    setIsOpen(false);
  });

  const popupPosition = useMemo((): {top: number; left: number} | null => {
    if (!isOpen || !swatchRef.current) {
      return null;
    }

    const rect = swatchRef.current.getBoundingClientRect();

    return {top: rect.bottom + POPUP_GAP_PX, left: rect.left - POPUP_GAP_PX};
  }, [isOpen]);

  const onSwatchClick = (event: React.MouseEvent): void => {
    event.stopPropagation();
    setIsOpen((prev) => !prev);
  };

  const onChange = (next: string): void => {
    setLiveColor(next);
    applyColor(editor, getSavedDescriptionRange(editor), next);
  };

  return (
    <>
      <button
        aria-label={window.i18next.t('pmwjs_form_maker_control_text_color_pick')}
        className="pmw-fm-rte-color-swatch-wrap pmw-fm-rte-color-swatch"
        onClick={onSwatchClick}
        onMouseDown={(e): void => {
          e.preventDefault();
        }}
        ref={swatchRef}
        style={{backgroundColor: liveColor}}
        type="button"
      />
      {renderPopup(isOpen, popupPosition, popupContainerRef, liveColor, onChange)}
    </>
  );
}

function renderPopup(
  isOpen: boolean,
  position: {top: number; left: number} | null,
  containerRef: React.RefObject<HTMLDivElement | null>,
  color: string,
  onChange: (next: string) => void
): ReactElement | null {
  if (!isOpen || !position) {
    return null;
  }

  return createPortal(
    <div
      className="pmw-fm-rte-color-popup radius-8 spacing-p-3"
      onMouseDown={(e): void => {
        e.stopPropagation();
      }}
      ref={containerRef}
      style={{position: 'fixed', top: position.top, left: position.left, zIndex: 10000}}
    >
      <ColorPickerPopup color={color} isAlphaDisabled onChange={onChange} onChangeEnded={onChange} />
    </div>,
    document.body
  );
}

function useCurrentTextColor(editor: Editor, component: Component): string {
  const subscribe = useCallback(
    (onChange: () => void) => {
      const doc = editor.Canvas.getBody()?.ownerDocument;
      doc?.addEventListener('selectionchange', onChange);
      editor.on('component:selected component:deselected', onChange);
      component.on('change:style', onChange);

      return (): void => {
        doc?.removeEventListener('selectionchange', onChange);
        editor.off('component:selected component:deselected', onChange);
        component.off('change:style', onChange);
      };
    },
    [editor, component]
  );

  const getSnapshot = useCallback((): string => {
    const doc = editor.Canvas.getBody()?.ownerDocument;
    return getColorFillForCurrentText(doc?.getSelection() ?? null);
  }, [editor]);

  return useSyncExternalStore(subscribe, getSnapshot);
}

function getColorFillForCurrentText(selection: Selection | null): string {
  if (!selection || selection.rangeCount === 0) {
    return DEFAULT_TEXT_COLOR;
  }

  const node = resolveSelectionColorNode(selection.getRangeAt(0));
  if (!node) {
    return DEFAULT_TEXT_COLOR;
  }

  return colorToHexString(window.getComputedStyle(node).color);
}

function resolveSelectionColorNode(range: Range): HTMLElement | null {
  let node: Node = range.startContainer;

  if (node instanceof HTMLElement) {
    const child = node.childNodes[range.startOffset] ?? node.childNodes[range.startOffset - 1];
    if (child) {
      node = child;
    }
  }

  return node instanceof HTMLElement ? node : node.parentElement;
}
